import React, { useEffect, useState } from 'react';
import { Redirect, useLocalSearchParams } from 'expo-router';
import { useCasesApi } from '@/lib/api/useCases';
import VerticalTemplate from '@/components/Landing/VerticalTemplate';
import UseCasePageSkeleton from '@/components/Landing/UseCasePageSkeleton';
import { readUseCaseInitialData, markUseCaseReady } from '../../../lib/useCaseHydration.js';
import { markAppReady } from '@/lib/appReady';

export default function UseCasePage() {
  const { slug } = useLocalSearchParams();
  const slugKey = typeof slug === 'string' ? slug : (Array.isArray(slug) ? slug[0] : '');
  const [initialData] = useState(() => readUseCaseInitialData(slugKey));
  const [useCase, setUseCase] = useState(initialData?.useCase || null);
  const [relatedUseCases, setRelatedUseCases] = useState(initialData?.relatedUseCases || []);
  const [loading, setLoading] = useState(!initialData);
  const [notFound, setNotFound] = useState(false);
  const [redirect, setRedirect] = useState(null);

  useEffect(() => {
    let alive = true;
    if (initialData?.useCase.slug === slugKey) {
      setUseCase(initialData.useCase);
      setRelatedUseCases(initialData.relatedUseCases);
      setLoading(false);
      setNotFound(false);
      setRedirect(null);
      return undefined;
    }
    if (!slugKey) {
      setLoading(false);
      setNotFound(true);
      return undefined;
    }

    setLoading(true);
    setNotFound(false);
    setRedirect(null);

    (async () => {
      try {
        const data = await useCasesApi.getBySlug(slugKey);
        if (!alive) return;
        if (typeof data?.redirect === 'string' && /^\/use-cases\/[a-z0-9-]+$/.test(data.redirect)) {
          setRedirect(data.redirect);
          return;
        }
        if (!data?.useCase) {
          setNotFound(true);
          return;
        }
        setUseCase(data.useCase);
        setRelatedUseCases(Array.isArray(data.relatedUseCases) ? data.relatedUseCases : []);
      } catch {
        if (!alive) return;
        setNotFound(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => { alive = false; };
  }, [slugKey, initialData]);

  useEffect(() => {
    if (!loading && !notFound && !redirect && useCase?.slug === slugKey) {
      markUseCaseReady(slugKey);
      markAppReady();
    }
  }, [loading, notFound, redirect, useCase, slugKey]);

  if (loading) {
    return <UseCasePageSkeleton />;
  }

  if (redirect) return <Redirect href={redirect} />;
  if (notFound || !useCase) {
    return <Redirect href="/use-cases" />;
  }
  if (useCase.slug !== slugKey) return <UseCasePageSkeleton />;

  return <VerticalTemplate key={slugKey} data={useCase} relatedUseCases={relatedUseCases} />;
}
